'use client';

import { ChevronDown, ChevronLeft, LayoutDashboard, LogOutIcon, User } from 'lucide-react';
import Link from 'next/link';
import { Heading } from '@/components/ui/Heading';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/authContext';
import SelectLanguage from '@/components/account/SelectLanguage';
import { normalizeLink } from '@/core/lib/utils';

type SidebarDetailsProps = {
  selectedItem?: MenuItem;
};

export default function SecondaryDrawer({ selectedItem }: SidebarDetailsProps) {
  const { primaryOpen, secondaryOpen, setSecondaryOpen, setPrimaryOpen } =
    useDrawerStore();
  const { dictionary } = useDictionary()
  const { state, clearAuth, hasPermission } = useAuth();
  const isLoggedIn = state?.isAuthenticated;
  const user = state?.user;

  const sortedChildren = [...(selectedItem?.children ?? [])].sort((a, b) => {
    if (a.imageUrl && !b.imageUrl) return 1;
    if (!a.imageUrl && b.imageUrl) return -1;
    return 0;
  });

  function handleClose() {
    setSecondaryOpen(false);
    setPrimaryOpen(false);
  }

  return (
    <div
      className={`
    transition-all no-scrollbar duration-300 ease-in-out bg-white overflow-y-auto
    fixed top-18 h-full z-60
    w-full lg:w-80 
    ltr:left-0 rtl:right-0 
    lg:ltr:left-80 lg:rtl:right-80
    ltr:border-r rtl:border-l
    ${primaryOpen && secondaryOpen
          ? 'translate-x-0 opacity-100 pointer-events-auto'
          : 'ltr:-translate-x-full rtl:translate-x-full opacity-0 pointer-events-none'
        }
  `}
      role="navigation"
      aria-label="Secondary navigation drawer"
    >
      <div className="p-4 flex flex-col min-h-[91%]">
        <div className='flex flex-1 flex-col'>
          {selectedItem ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <div className='flex items-center gap-2' onClick={() => setSecondaryOpen(false)}
                  aria-label="Back to primary drawer">
                  <button
                  >
                    <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
                  </button>
                  <Heading
                    level={3}
                  >
                    {selectedItem.title}
                  </Heading>
                </div>
                <Link
                  href={normalizeLink(selectedItem.link) || "#"}
                  className="flex items-center justify-between gap-1 hover:underline"
                >
                  <Heading level={3}>
                    {dictionary.ui.pages.seeAll}
                  </Heading>
                  <ChevronLeft className="w-4 h-4 ltr:rotate-180" />
                </Link>
              </div>
              <ul className="flex flex-col gap-y-1">
                {sortedChildren.map(child => (
                  <li key={child.id} onClick={handleClose}>
                    <Link
                      href={normalizeLink(child.link)}
                      className="flex items-center p-1 text-primary-600"
                    >
                      <p>
                        {child.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
        <div className='flex justify-between items-center border-t border-secondary-100 pt-4'>
          <div>
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="User menu"
                    className="flex items-center justify-center hover:text-primary transition"
                  >
                    <User className="w-5 h-5 stroke-1" />
                    <ChevronDown className="size-4 stroke-1" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="center"
                  sideOffset={6}
                  className="w-36 p-1.5 text-sm shadow-md border border-gray-100"
                >
                  <DropdownMenuLabel className="px-2 py-1.5 text-[13px] text-primary-600 truncate">
                    {user?.full_name || 'کاربر'}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="my-1" />

                  <DropdownMenuItem
                    asChild
                    className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-gray-100"
                  >
                    <Link href="/profile" className="flex items-center gap-2">
                      {dictionary.forms.profile}
                      <User className="size-4 text-primary-900 stroke-1" />
                    </Link>
                  </DropdownMenuItem>

                  {hasPermission("AccessToAdmin") && (
                    <DropdownMenuItem
                      asChild
                      className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-gray-100"
                    >
                      <Link href="/admin" className="flex items-center gap-2">
                        {dictionary.ui.profile.adminPanel}
                        <LayoutDashboard className="size-4 text-primary-900 stroke-1" />
                      </Link>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator className="my-1" />

                  <DropdownMenuItem
                    onClick={clearAuth}
                    className="px-2 py-1.5 cursor-pointer text-[13px] text-red-600 hover:bg-red-50 focus:text-red-600"
                  >
                    {dictionary.common.logout}
                    <LogOutIcon className="size-4 text-red-600 stroke-1" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link href="/login" aria-label="User login">
                {/* <User className="w-5 h-5 stroke-1" /> */}
                {dictionary.forms.login}
              </Link>
            )}
          </div>
          <SelectLanguage />
        </div>
      </div>
    </div>
  );
}
